import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Eyebrow from '../components/common/Eyebrow';
import { SpecialsShimmer } from '../components/shimmer';
import { useListOffersQuery } from '../store/siteContentApi';
import { buildRoute, ROUTES } from '../constants/routes';

// ─── Helpers ────────────────────────────────────────────────
const fmtINR = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

// Mirrors admin-side `computeFinal`. Kept duplicated (not a shared util)
// because the two apps ship separately and the math is trivial.
const computeFinal = (o) => {
  const base = Number(o?.original_price) || 0;
  const v = Number(o?.offer_value) || 0;
  if (o?.offer_type === 'flat') return Math.max(0, base - v);
  return Math.max(0, Math.round(base * (1 - v / 100)));
};

// "40% OFF" / "₹500 OFF" style badge — short form.
const shortDiscount = (o) => {
  if (!o) return '';
  if (o.offer_type === 'flat') return `₹${Number(o.offer_value).toLocaleString('en-IN')} OFF`;
  return `${Number(o.offer_value)}% OFF`;
};

// Humanise the validity end — "Ends 30 Apr" (or empty if open-ended).
const formatValidity = (iso) => {
  if (!iso) return 'Limited time';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'Limited time';
  return `Ends ${d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}`;
};

// Deterministic fallback emoji when the admin doesn't set a badge —
// keeps cards visually rich without an extra field.
const emojiFor = (o) => {
  const key = (o.campaign || o.test_name || '').toLowerCase();
  if (key.includes('women'))    return '🌸';
  if (key.includes('summer'))   return '☀️';
  if (key.includes('diabet'))   return '🔬';
  if (key.includes('heart') || key.includes('cardi')) return '❤️';
  if (key.includes('thyroid'))  return '🦋';
  if (key.includes('vitamin'))  return '💊';
  return '🧪';
};

const Specials = () => {
  const navigate = useNavigate();
  const { data: offers = [], isLoading } = useListOffersQuery();
  const [activeCampaign, setActiveCampaign] = useState(null); // null = "All"

  // Derive campaign pills from the active offers. An "All" option sits at
  // the front; remaining pills are whatever unique, non-empty campaign
  // tags admins have assigned.
  const campaigns = useMemo(() => {
    const seen = new Map();
    for (const o of offers) {
      const key = (o.campaign || '').trim();
      if (!key) continue;
      if (!seen.has(key)) seen.set(key, key);
    }
    return ['All', ...Array.from(seen.keys())];
  }, [offers]);

  const visibleOffers = useMemo(() => {
    if (!activeCampaign || activeCampaign === 'All') return offers;
    return offers.filter((o) => (o.campaign || '').trim() === activeCampaign);
  }, [offers, activeCampaign]);

  const heroSavings = useMemo(() => {
    if (!visibleOffers.length) return 0;
    const max = visibleOffers.reduce((m, o) => {
      if (o.offer_type !== 'percent') return m;
      return Math.max(m, Number(o.offer_value) || 0);
    }, 0);
    return max;
  }, [visibleOffers]);

  return (
    <Box>
      {/* Hero */}
      <Box sx={{ bgcolor: 'var(--forest)', pt: { xs: 10, md: 13 }, pb: 5, px: { xs: 2, md: 7 } }}>
        <Container maxWidth="xl" sx={{ maxWidth: 1300 }}>
          <Eyebrow white>Limited Time</Eyebrow>
          <Typography variant="h1" className="serif" sx={{ color: '#fff', mt: 1.5, mb: 1 }}>
            Special Offers
          </Typography>
          <Typography sx={{ fontSize: 14, color: 'rgba(255,255,255,0.4)', mb: 4, maxWidth: 520, fontWeight: 300 }}>
            Hand-picked tests at exclusive prices. Home collection included. NABL verified.
          </Typography>

          {campaigns.length > 1 && (
            <Stack direction="row" spacing={1.25} sx={{ flexWrap: 'wrap', gap: 1 }}>
              {campaigns.map((c) => {
                const isActive = (c === 'All' && !activeCampaign) || activeCampaign === c;
                return (
                  <Button
                    key={c}
                    onClick={() => setActiveCampaign(c === 'All' ? null : c)}
                    sx={{
                      borderRadius: 100,
                      px: 2.5, py: 1,
                      border: '1.5px solid',
                      borderColor: isActive ? 'var(--sage)' : 'rgba(255,255,255,0.2)',
                      bgcolor: isActive ? 'var(--sage)' : 'transparent',
                      color: isActive ? '#fff' : 'rgba(255,255,255,0.7)',
                      fontSize: 13, fontWeight: 700, textTransform: 'none',
                      '&:hover': {
                        borderColor: 'var(--sage-l)',
                        bgcolor: isActive ? 'var(--sage)' : 'rgba(255,255,255,0.04)',
                      },
                    }}
                  >
                    {c}
                  </Button>
                );
              })}
            </Stack>
          )}
        </Container>
      </Box>

      {/* Offers grid */}
      <Container maxWidth="xl" sx={{ maxWidth: 1300, py: 5, px: { xs: 2, md: 4 } }}>
        <Box sx={{ mb: 3.5 }}>
          <Typography sx={{ fontSize: 18, fontWeight: 700, color: 'var(--ink)' }}>
            {activeCampaign || 'All offers'}
          </Typography>
          <Typography sx={{ fontSize: 13, color: 'var(--stone)' }}>
            {visibleOffers.length} offer{visibleOffers.length === 1 ? '' : 's'} · All prices include home collection
            {heroSavings > 0 && ` · Save up to ${heroSavings}%`}
          </Typography>
        </Box>

        {isLoading ? (
          <SpecialsShimmer count={6} />
        ) : visibleOffers.length === 0 ? (
          <Box sx={{
            bgcolor: '#fff', border: '1px dashed var(--border-l)', borderRadius: '18px',
            py: 7, textAlign: 'center',
          }}>
            <Typography sx={{ fontSize: 40, mb: 1.5 }}>🏷️</Typography>
            <Typography sx={{ fontSize: 16, fontWeight: 700, color: 'var(--ink)', mb: 0.75 }}>
              No offers running right now
            </Typography>
            <Typography sx={{ fontSize: 13, color: 'var(--stone)', maxWidth: 340, mx: 'auto' }}>
              New specials go live here as soon as we launch them — check back soon.
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
              gap: { xs: 2, md: 2.5 },
              width: '100%',
            }}
          >
            {visibleOffers.map((o) => {
              const final = computeFinal(o);
              const hasOriginal = Number(o.original_price) > 0 && final < Number(o.original_price);
              return (
                <Box
                  key={o._id}
                  onClick={() => navigate(buildRoute(ROUTES.TEST_DETAIL, { id: o.test_id }))}
                  sx={{
                    bgcolor: '#fff',
                    border: '1.5px solid var(--border-l)',
                    borderRadius: '20px',
                    p: { xs: 2.5, md: 3.25 },
                    cursor: 'pointer',
                    position: 'relative',
                    overflow: 'hidden',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0, left: 0, right: 0,
                      height: 4,
                      background: 'linear-gradient(90deg, var(--sage), var(--sage-p))',
                    },
                    '&:hover': {
                      borderColor: 'var(--sage-p)',
                      transform: 'translateY(-4px)',
                      boxShadow: '0 12px 36px rgba(61,122,82,0.12)',
                    },
                    transition: 'all 0.28s',
                  }}
                >
                  {/* Badge — admin-supplied, falls back to the discount */}
                  <Box sx={{
                    position: 'absolute', top: 14, right: 14,
                    fontSize: 10, fontWeight: 800, letterSpacing: '0.04em',
                    bgcolor: 'var(--amber)', color: '#fff',
                    px: 1, py: 0.4, borderRadius: 100,
                  }}>
                    {o.badge || shortDiscount(o)}
                  </Box>

                  <Typography sx={{ fontSize: 36, mb: 1.5 }}>{emojiFor(o)}</Typography>

                  <Typography sx={{ fontSize: 15.5, fontWeight: 700, color: 'var(--ink)', mb: 0.5, pr: 6 }}>
                    {o.title || o.test_name}
                  </Typography>

                  {o.description && (
                    <Typography sx={{ fontSize: 12.5, color: 'var(--stone)', fontWeight: 300, mb: 1.5, lineHeight: 1.55 }}>
                      {o.description}
                    </Typography>
                  )}

                  <Typography sx={{ fontSize: 11.5, color: 'var(--stone)', mb: 2 }}>
                    {formatValidity(o.valid_until)}
                    {o.campaign && ` · ${o.campaign}`}
                  </Typography>

                  <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 2, mt: 'auto' }}>
                    <Typography className="serif" sx={{ fontSize: 30, fontWeight: 700, color: 'var(--forest)', lineHeight: 1 }}>
                      {fmtINR(final)}
                    </Typography>
                    {hasOriginal && (
                      <Typography sx={{ fontSize: 13, textDecoration: 'line-through', color: 'var(--stone)' }}>
                        {fmtINR(o.original_price)}
                      </Typography>
                    )}
                  </Stack>

                  <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(buildRoute(ROUTES.TEST_DETAIL, { id: o.test_id }));
                    }}
                    sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 100, py: 1.125 }}
                  >
                    View Details →
                  </Button>
                </Box>
              );
            })}
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default Specials;
